// **BRAND MANAGEMENT TYPES & API**

export interface Brand {
  _id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export interface BrandFilters {
  search?: string;
  isActive?: boolean;
}

export interface BrandImportData {
  name: string;
}

export interface BrandImportResult {
  successful: number;
  failed: number;
  created: Brand[];
  errors: Array<{
    row: number;
    name: string;
    error: string;
  }>;
}

// Brand API Functions
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const getBrands = async (filters?: BrandFilters): Promise<Brand[]> => {
  const token = localStorage.getItem('token');
  
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  
  const response = await fetch(`${API_BASE_URL}/brands?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch brands');
  }

  return response.json();
};

export const createBrand = async (brandData: Partial<Brand>): Promise<Brand> => {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_BASE_URL}/brands`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(brandData),
  });

  if (!response.ok) {
    throw new Error('Failed to create brand');
  }

  return response.json();
};

export const updateBrand = async (id: string, brandData: Partial<Brand>): Promise<Brand> => {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_BASE_URL}/brands/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(brandData),
  });

  if (!response.ok) {
    throw new Error('Failed to update brand');
  }

  return response.json();
};

export const deleteBrand = async (id: string): Promise<void> => {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_BASE_URL}/brands/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to delete brand');
  }
};

export const importBrands = async (data: BrandImportData[]): Promise<BrandImportResult> => {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_BASE_URL}/brands/import`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ brands: data }),
  });

  if (!response.ok) {
    throw new Error('Failed to import brands');
  }

  return response.json();
};

// Helper function to create brand if it doesn't exist
export const getOrCreateBrand = async (brandName: string): Promise<Brand> => {
  try {
    const brands = await getBrands({ search: brandName });
    const existingBrand = brands.find(b => b.name.toLowerCase() === brandName.toLowerCase());
    
    if (existingBrand) {
      return existingBrand;
    }
    
    // Create new brand
    return await createBrand({ name: brandName, isActive: true });
  } catch (error) {
    console.error('Error getting or creating brand:', error);
    throw error;
  }
};

export default {
  getBrands,
  createBrand,
  updateBrand,
  deleteBrand,
  importBrands,
  getOrCreateBrand
};
